import type { AxiosError } from 'axios'
import type {
  AksiNyataSession,
  Material,
  MateriContent,
  Module,
  ModuleReviewer,
  PostTestState,
  Question,
} from 'types/microlearning'

interface MILError {
  code: string
  message: string
}

export interface MilErrorResponse {
  errors: MILError[]
  meta?: any
}

export interface MilUseQueryError extends AxiosError {
  originalError: {
    response: {
      data: MilErrorResponse | Partial<MilErrorResponse>
    }
  }
}

export interface ErrorItems {
  code: string
  message: string
}

export interface CommonErrorResponse {
  originalError: {
    response: {
      data: {
        errors: ErrorItems[]
      }
    }
  }
}

export interface ModuleResponse {
  modules: Module[]
  name: string
  values: string[]
  aksiNyataOptionCount?: number
}

export interface PostRefleksiResponse {
  data: {
    ID: string
    reflective: { answer: string }
  }
}

export interface PostRefleksi {
  answer: string
}

export interface FailedMaterial {
  id: number
  title: string
}

export interface CreatePostTestSessionData {
  id: number
  moduleId: number
  questions: Question[]
  sessionComplete: boolean
  state: PostTestState
  failedMaterial?: FailedMaterial[]
}

export interface PostTestAnswerParams {
  questionId: number
  answer: String[]
}

export interface PostTestRequestBody {
  posttest: PostTestAnswerParams[]
}

export interface ResponseQuiz {
  name: string
  questions: Question[]
  completed?: boolean
}

export interface MaterialDataResponse {
  id: string
  name: string
  completed: boolean
  reflective: ResponseReflective
  contents: MateriContent[]
  quiz: ResponseQuiz
  shouldRetry: boolean
}

export interface ResponseMaterialTextData {
  id: string
  name: string
  contents: string[]
}

export interface ResponseMaterialVideoData {
  id: string
  name: string
  url: string
  description: string
  isVertical: boolean
}

export interface ResponseModulData {
  id: string
  name: string
  description: string
  reviewer: ModuleReviewer
  learningMaterial: {
    completed: boolean
    count: number
    materials: Material[]
  }
  postTest: {
    name: string
    state: PostTestState
  }
}

export interface ResponseReflective {
  question: string
  answer: string
  completed?: boolean
}

export interface ResponseModulProgress {
  moduleId: number
  material: boolean
  postTest: boolean
  prerequisiteCompleted: boolean
}

export interface ResponseTopicProgress {
  id: number
  percentage?: number
  modules: ResponseModulProgress[]
  aksiNyata: AksiNyataSession
}

export interface MateriReflection {
  question: string
  answer: string
  completed: boolean
}

export interface TopicSpeedKV {
  generic_info: string
  topics: {
    id: string
    recommendations?: {
      aksi_nyata?: string
      pelajari_semua_modul?: string
      rekomendasi_penyelesaian_topik?: string
    }
  }[]
}

export enum JenjangValid {
  SLB = 'slb',
  PAUD = 'paud',
  SD = 'sd',
  SMP = 'smp',
  SMK = 'smk',
  SMA = 'sma',
  NONJENJANG = '',
}
export interface WebinarModule {
  id: number
  jenjang: Array<JenjangValid>
  title: string
  topikId: number
  topikTitle: string
  // to be deprecated when CMS webinar is fully rolled out
  moduleId?: number
}

export interface WebinarLinks {
  interaction?: string
  youtube?: string
  zoom?: string
}

export interface WebinarSpeaker {
  avatar: string
  name: string
  profesi: string
}

export interface WebinarAdditionalLink {
  title: string
  description: string
  buttonLabel: string
  buttonTarget: string
}

export interface WebinarEntry {
  id: string
  title: string
  description: string
  subtitle: string
  startDate: string
  endDate: string
  isOpen: boolean
  links: WebinarLinks
  module: WebinarModule[]
  speakers: WebinarSpeaker[]
  // to be deprecated when CMS webinar is fully rolled out
  topic?: {
    id: number
    title: string
  }
  additionalLinks?: WebinarAdditionalLink[]
}

export const CERTIFICATE_STATUS = {
  AVAILABLE: 'AVAILABLE',
  PENDING: 'PENDING',
  NOT_AVAILABLE: 'NOT_AVAILABLE',
} as const

export type CertificateStatusType = keyof typeof CERTIFICATE_STATUS
